import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { useEffect } from 'react';

const API = '/api';

// 3 Sept 2026 — redirect-after-login. Only ever trust ?redirect= as far as "a single
// in-app relative path" — must start with exactly one "/", never "//" or "/\" (both are
// protocol-relative-URL tricks some browsers will still treat as off-site), and never
// contain an embedded "://". Anything else is discarded and the existing role-based
// default below applies instead, same as before this existed.
function safeRedirectPath(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (/^\/(?!\/|\\)\S*$/.test(raw) && !raw.includes('://')) return raw;
  return null;
}

export default function Login() {
  const rejected = new URLSearchParams(window.location.search).get('rejected') === '1';
  const sessionExpired = new URLSearchParams(window.location.search).get('expired') === '1';
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [tenantBranding, setTenantBranding] = useState(null); // { name, logo_url } | null
  const [loading, setLoading]   = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    fetch('/api/company-profile/by-hostname')
      .then(r => r.ok ? r.json() : { company: null })
      .then(d => setTenantBranding(d.company || null))
      .catch(() => {});
  }, []);

  // Two-factor challenge (shown after a correct password if the account has 2FA enabled)
  const [step, setStep]               = useState('credentials'); // credentials | challenge
  const [pendingToken, setPendingToken] = useState('');
  const [methods, setMethods]         = useState([]);
  const [method, setMethod]           = useState('totp');
  const [code, setCode]               = useState('');
  const [challengeError, setChallengeError]     = useState('');
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [smsSent, setSmsSent]         = useState(false);
  const [smsSending, setSmsSending]   = useState(false);

  const completeLogin = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    const { role } = data.user;
    // If company mandates 2FA and user hasn't set it up, send them to Settings — takes
    // priority over any pending redirect, they need to finish 2FA setup first.
    if (data.mustSetup2FA) {
      navigate('/settings?require2fa=1');
      return;
    }
    // completeLogin is the one funnel both a plain password login and a completed 2FA
    // challenge land in, so this covers both without extra wiring. Falls through to the
    // existing role-based default whenever there's no (valid) redirect param — unchanged
    // behaviour for a normal, non-deep-linked login.
    const redirectTo = safeRedirectPath(new URLSearchParams(window.location.search).get('redirect'));
    if (redirectTo) { navigate(redirectTo); return; }
    if (role === 'platform_owner') navigate('/owner');
    else if (role === 'service_partner') navigate('/partner/portal');
    else if (role === 'introducer') navigate('/introducer-dashboard');
    else navigate('/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authService.login(email, password);
      if (response.data.requires2FA) {
        setPendingToken(response.data.pendingToken);
        setMethods(response.data.methods || ['totp', 'backup_code']);
        setMethod('totp');
        setCode('');
        setChallengeError('');
        setSmsSent(false);
        setStep('challenge');
        return;
      }
      completeLogin(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setChallengeError('');
    setChallengeLoading(true);
    try {
      const res = await fetch(`${API}/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingToken, method, code }),
      });
      const data = await res.json();
      if (!res.ok) { setChallengeError(data.error || 'Invalid code'); return; }
      completeLogin(data);
    } catch {
      setChallengeError('Network error.');
    } finally {
      setChallengeLoading(false);
    }
  };

  const handleSendSms = async () => {
    setChallengeError('');
    setSmsSending(true);
    try {
      const res = await fetch(`${API}/2fa/send-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingToken }),
      });
      const data = await res.json();
      if (!res.ok) { setChallengeError(data.error || 'Could not send SMS code.'); return; }
      setMethod('sms');
      setCode('');
      setSmsSent(true);
    } catch {
      setChallengeError('Network error.');
    } finally {
      setSmsSending(false);
    }
  };

  const backToPassword = () => {
    setStep('credentials');
    setPendingToken(''); setMethods([]); setMethod('totp'); setCode('');
    setChallengeError(''); setSmsSent(false);
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0e1b2c',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: '40px 36px',
        width: '100%', maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {tenantBranding?.logo_url ? (
            <img src={tenantBranding.logo_url} alt={tenantBranding.name} style={{ maxWidth: 160, maxHeight: 64, margin: '0 auto 16px', display: 'block' }} />
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: '#10b981', margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28,
            }}>🔗</div>
          )}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0e1b2c' }}>{tenantBranding?.name || 'BrokerConnect'}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#718096' }}>Sign in to your account</p>
        </div>

        {step === 'credentials' && sessionExpired && (
          <div style={{
            background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8,
            padding: '14px 16px', marginBottom: 20, fontSize: 13, color: '#1e3a8a', lineHeight: 1.6,
          }}>
            Your session expired. Please sign in again.
          </div>
        )}

        {step === 'credentials' && rejected && (
          <div style={{
            background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
            padding: '14px 16px', marginBottom: 20, fontSize: 13, color: '#78350f', lineHeight: 1.6,
          }}>
            <strong style={{ display: 'block', marginBottom: 4, color: '#92400e' }}>About your introducer application</strong>
            Your application wasn't approved this time. This can happen for a number of reasons, and it may
            simply need a correction. Please contact your broker directly — they can explain why and
            send you a fresh invite if appropriate.
          </div>
        )}

        {step === 'credentials' && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#4b5a6e', marginBottom: 6 }}>
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14,
                  border: '1px solid #E2E8F0', borderRadius: 8,
                  color: '#0e1b2c', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#4b5a6e', marginBottom: 6 }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14,
                  border: '1px solid #E2E8F0', borderRadius: 8,
                  color: '#0e1b2c', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {error && (
              <div style={{
                marginBottom: 16, padding: '10px 14px',
                background: '#fee2e2', border: '1px solid #fca5a5',
                borderRadius: 8, fontSize: 13, color: '#991b1b', fontWeight: 500,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '12px',
                background: loading ? '#a0aec0' : '#10b981',
                color: '#fff', border: 'none', borderRadius: 8,
                fontWeight: 600, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {step === 'challenge' && (
          <form onSubmit={handleVerifyCode}>
            <p style={{ fontSize: 13, color: '#718096', margin: '0 0 20px', textAlign: 'center' }}>
              {method === 'sms'
                ? 'Enter the code we texted you.'
                : method === 'backup_code'
                ? 'Enter one of your backup codes.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>

            <div style={{ marginBottom: 20 }}>
              <input
                type="text"
                inputMode={method === 'backup_code' ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                placeholder={method === 'backup_code' ? 'A1B2C3D4' : '123456'}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 18, letterSpacing: '0.15em', textAlign: 'center',
                  border: '1px solid #E2E8F0', borderRadius: 8,
                  color: '#0e1b2c', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {challengeError && (
              <div style={{
                marginBottom: 16, padding: '10px 14px',
                background: '#fee2e2', border: '1px solid #fca5a5',
                borderRadius: 8, fontSize: 13, color: '#991b1b', fontWeight: 500,
              }}>
                {challengeError}
              </div>
            )}

            <button
              type="submit"
              disabled={challengeLoading}
              style={{
                width: '100%', padding: '12px',
                background: challengeLoading ? '#a0aec0' : '#10b981',
                color: '#fff', border: 'none', borderRadius: 8,
                fontWeight: 600, fontSize: 15, cursor: challengeLoading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {challengeLoading ? 'Verifying…' : 'Verify & sign in'}
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20, alignItems: 'center' }}>
              {method !== 'totp' && (
                <button type="button" onClick={() => { setMethod('totp'); setCode(''); setChallengeError(''); }}
                  style={{ background: 'none', border: 'none', color: '#10b981', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  Use authenticator app instead
                </button>
              )}
              {method !== 'sms' && methods.includes('sms') && (
                <button type="button" onClick={handleSendSms} disabled={smsSending}
                  style={{ background: 'none', border: 'none', color: '#10b981', fontSize: 13, fontWeight: 500, cursor: smsSending ? 'not-allowed' : 'pointer' }}>
                  {smsSending ? 'Sending…' : 'Text me a code instead'}
                </button>
              )}
              {smsSent && method === 'sms' && (
                <span style={{ fontSize: 12, color: '#a0aec0' }}>Code sent — check your phone.</span>
              )}
              {method !== 'backup_code' && (
                <button type="button" onClick={() => { setMethod('backup_code'); setCode(''); setChallengeError(''); }}
                  style={{ background: 'none', border: 'none', color: '#718096', fontSize: 13, cursor: 'pointer' }}>
                  Use a backup code
                </button>
              )}
              <button type="button" onClick={backToPassword}
                style={{ background: 'none', border: 'none', color: '#a0aec0', fontSize: 12, cursor: 'pointer', marginTop: 8 }}>
                ← Back to password
              </button>
            </div>
          </form>
        )}

        {step === 'credentials' && (
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: '#a0aec0' }}>
            <a href="/forgot-password" style={{ display: 'block', textAlign: 'center', marginBottom: 12, color: '#10b981', fontSize: 13, textDecoration: 'none', fontWeight: 500 }}>Forgotten your password?</a>
            BrokerConnect · Mortgage Referral Platform
          </p>
        )}
      </div>
    </div>
  );
}
